import { useCallback } from 'react'

import { useFragment } from '@apollo/client'
import { Box, Text } from '@factorialco/deprecated-design-system'
import { RichTextDisplay } from '@factorialco/f0-react/dist/experimental'
import { TasksValueObjectsTaskStatusEnum } from 'generated/resources/datatable.graphql'
import { ManualTaskContentFragmentDoc } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import TaskFooter from 'modules/inbox_tasks/activities/Task/Footer'
import {
  PossibleTaskState,
  TaskCustomComponent,
  TaskDefinition,
  TaskFooterComponent,
} from 'modules/inbox_tasks/lib/abstraction'
import { useUpdateTaskStatuses } from 'modules/inbox_tasks/lib/updateTaskStatus/updateTaskStatus'

type State = PossibleTaskState

const WorkflowHandlerExecutionTaskContent: TaskCustomComponent<
  'WorkflowsWorkflowHandlerExecution',
  State
> = ({ taskId }) => {
  const { data: taskInfo } = useFragment({
    fragment: ManualTaskContentFragmentDoc,
    fragmentName: 'ManualTaskContent',
    from: {
      __typename: 'TasksInboxTask',
      id: taskId,
    },
    optimistic: false,
  })

  if (!taskInfo) return null

  return (
    <Box flexDirection="column" gap="s32" overflowX="hidden">
      {taskInfo.content && (
        <Box flexDirection="column" gap="s4">
          <Text.Header3>{i18n.t('common.description')}</Text.Header3>
          <RichTextDisplay content={taskInfo.content} className="text-f1-foreground-secondary" />
        </Box>
      )}
    </Box>
  )
}

const WorkflowHandlerExecutionTaskFooter: TaskFooterComponent<
  'WorkflowsWorkflowHandlerExecution',
  State
> = ({ taskId, status }) => {
  const updateTaskStatuses = useUpdateTaskStatuses()

  const handleDropdownAction = useCallback(
    async ({ actionId }: { actionId: string }) => {
      const statuses: Record<string, TasksValueObjectsTaskStatusEnum> = {
        'mark-as-done': 'done',
        'mark-as-todo': 'todo',
        'mark-as-in-progress': 'in_progress',
        'mark-as-discarded': 'discarded',
      }

      const newStatus = statuses[actionId]

      if (!newStatus) return

      await updateTaskStatuses({
        taskIds: [taskId],
        status: newStatus,
      })

      return true
    },
    [updateTaskStatuses, taskId]
  )

  return (
    <TaskFooter
      taskId={taskId}
      taskKind="manual"
      status={status}
      onClickDropdownAction={handleDropdownAction}
    />
  )
}

export const WorkflowHandlerExecutionTaskDefinition: TaskDefinition<
  'WorkflowsWorkflowHandlerExecution',
  State
> = {
  taskableTypename: 'WorkflowsWorkflowHandlerExecution',
  initialState: {},
  component: WorkflowHandlerExecutionTaskContent,
  footer: WorkflowHandlerExecutionTaskFooter,
}
